<template lang="pug">
el-form.c-claim-details-form
  app-main-content-layout(:title='item?.causeName' back-to='/manage/benevity-claim')

    //- Left Column
    template(#col-left)
      .c-content-section
        .c-section-header
          h5.c-section-title General

        .organization-logo.mb-4
          img(v-if="item?.logo" :src="item?.logo" alt="Organization Logo")
          img(v-else src="@/assets/images/default-profile.jpeg" alt="Organization Logo")

        el-form-item(label="Organization Name")
          el-input(:value="item?.causeName" readonly)

        el-form-item(label="About")
          el-input(:value="item?.description" type="textarea" readonly :rows="6")

        el-form-item.mb-0
          template(#label)
            | Our Ethos
            el-tooltip(
              effect="customized"
              :content="ethosTooltip"
              placement="top"
              popper-class="max-w-350"
            )
              fai(type="fal" icon="info-circle").size-18.ms-2
          el-input(:value="item?.ethos" type="textarea" readonly :rows="4")

    //- Right Column
    template(#col-right)
      .c-content-section
        .c-section-header
          h5.c-section-title Claim Request

        .c-detail-item
          .c-detail-label Business Email
          .c-detail-value {{ item?.eMail }}

        .c-detail-item
          .c-detail-label EIN
          .c-detail-value {{ item?.ein }}

        .c-detail-item(v-if="!hideStatusSelect")
          .c-detail-label Status
          VueMultiselect(
            v-model="selectedStatus"
            :options="statusOptions"
            :loading="isLoadingStatusChange"
            label="label"
            track-by="value"
            :multiple="false"
            placeholder="Select action..."
          )

        .c-detail-item(v-else)
          .c-detail-label Status
          .c-detail-value {{ item?.claimStatus }}

      .c-content-section.mt-3
        .c-section-header
          h5.c-section-title More Information

        .c-detail-item
          .c-detail-label Organization Type
          .c-detail-value Nonprofit

        .c-detail-item
          .c-detail-label Admin Email
          .c-detail-value {{ item?.adminEmail }}

        .c-detail-item.mb-0
          .c-detail-label Cause ID
          .c-detail-value {{ item?.causeId }}
</template>

<script>
import {
  onMounted, reactive, ref, watch,
} from 'vue';
import VueMultiselect from 'vue-multiselect';
import globalShared from '@/shared';
import shared from '@/views/manage-users-and-organizations/shared';

export default {
  name: 'ClaimRequestDetails',
  components: {
    VueMultiselect,
  },
  setup() {
    const isLoading = ref(false);
    const isLoadingStatusChange = ref(false);
    const hideStatusSelect = ref(true);
    const selectedStatus = ref(null);
    const item = reactive({});
    const ethosTooltip = 'This section describes...';
    const statusOptions = [
      { value: 'Approved', label: 'Approve' },
      { value: 'Declined', label: 'Decline' },
    ];

    const { route } = globalShared();
    const { getClaimRequestDetails, mutateClaimStatus } = shared();
    const { claimRequestId } = route.params;

    async function updateClaimStatus() {
      isLoadingStatusChange.value = true;
      try {
        await mutateClaimStatus({ id: claimRequestId, status: selectedStatus.value.value });
      } finally {
        isLoadingStatusChange.value = false;
        item.claimStatus = selectedStatus.value.value;
        hideStatusSelect.value = true;
      }
    }

    onMounted(async () => {
      isLoading.value = true;
      try {
        const response = await getClaimRequestDetails(claimRequestId);
        item.logo = response.logo;
        item.causeName = response.causeName;
        item.causeId = response.causeId;
        item.description = response.description;
        item.eMail = response.eMail;
        item.ein = response.ein;
        item.claimStatus = response.claimStatus;
        item.adminEmail = response.userInfo?.email || 'N/A';
        hideStatusSelect.value = response.claimStatus !== 'Pending';
      } finally {
        isLoading.value = false;
      }
    });

    watch(() => selectedStatus.value, (newValue) => {
      if (newValue) {
        updateClaimStatus();
      }
    });

    return {
      item,
      isLoading,
      isLoadingStatusChange,
      ethosTooltip,
      statusOptions,
      hideStatusSelect,
      selectedStatus,
    };
  },
};
</script>

<style lang="scss" scoped>
@import '@/assets/styles/variables';

// Color Variables - Match OrgEdit.vue
$color-teal-accent: #01beb0;
$gray-50: #f9fafb;
$gray-100: #f3f4f6;
$gray-200: #e5e7eb;
$gray-300: #d1d5db;
$gray-400: #9ca3af;
$gray-500: #6b7280;
$gray-600: #4b5563;
$gray-700: #374151;
$gray-800: #1f2937;
$gray-900: #111827;
$color-purple-text: #4349dd;

.c-claim-details-form {
  padding: 2rem;

  :deep(.col-has-child) {
    display: flex;
    flex-direction: column;
    gap: 1rem;
  }
}

// White Content Sections
.c-content-section {
  background: white;
  border-radius: 14px;
  border: 1px solid rgba(0, 0, 0, 0.1);
  box-shadow: none;
  padding: 1.5rem;
  overflow: hidden;
}

// Section Header
.c-section-header {
  margin-bottom: 1.5rem;
  padding-bottom: 0.75rem;
  border-bottom: 1px solid #f0f0f0;
}

.c-section-title {
  font-size: 1rem;
  font-weight: 600;
  color: $gray-900;
  margin: 0;
  letter-spacing: -0.01em;
}

// Organization Logo
.organization-logo {
  display: flex;
  justify-content: center;
  align-items: center;

  img {
    width: 160px;
    height: 160px;
    border-radius: 50%;
    object-fit: cover;
    border: 3px solid #f0f0f3;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  }
}

// Detail Items
.c-detail-item {
  margin-bottom: 1.5rem;

  &:last-child {
    margin-bottom: 0;
  }
}

.c-detail-label {
  color: $gray-600;
  font-weight: 600;
  font-size: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 0.025em;
  margin-bottom: 0.5rem;
}

.c-detail-value {
  color: $gray-800;
  font-size: 0.875rem;
  font-weight: 500;
  line-height: 1.5;
  padding: 0.75rem 1rem;
  background: #f9fafb;
  border-radius: 8px;
  border: 1px solid rgba(0, 0, 0, 0.1);
}

// Form Styling
:deep(.el-form-item) {
  margin-bottom: 1.5rem;

  .el-form-item__label {
    color: #0a0a0a;
    font-weight: 500;
    font-size: 0.875rem;
    line-height: 1.5;
    margin-bottom: 0.5rem;
    display: flex;
    align-items: center;
    gap: 0.25rem;
  }

  .el-input__wrapper {
    border-radius: 8px;
    border: 1px solid transparent;
    background: #f3f3f5;
    box-shadow: none;
    padding: 0 12px;
    height: 36px;
    transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);

    &.is-disabled {
      border: 1px solid transparent;
      background: #f3f3f5;
      opacity: 0.8;
    }
  }

  .el-input__inner {
    height: 100%;
    font-size: 0.875rem;
    color: $gray-700;
    background: transparent;
    font-weight: 500;

    &::placeholder {
      color: $gray-400;
      font-weight: 400;
    }
  }

  .el-textarea__inner {
    border-radius: 8px;
    border: 1px solid transparent;
    background: #f3f3f5;
    box-shadow: none;
    padding: 12px;
    color: $gray-700;
    font-weight: 500;
    font-size: 0.875rem;
    line-height: 1.5;
    transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
    min-height: 100px;

    &::placeholder {
      color: $gray-400;
      font-weight: 400;
    }

    &[readonly] {
      opacity: 0.8;
    }
  }
}

// VueMultiselect Styling
:deep(.multiselect) {
  border-radius: 8px;
  border: 1px solid transparent;
  background: #f3f3f5;
  box-shadow: none;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);

  .multiselect__tags {
    border: none;
    background: transparent;
    padding: 8px 40px 0 12px;
    min-height: 36px;
    border-radius: 8px;
  }

  .multiselect__select {
    height: 36px;
    padding: 4px 8px;
  }

  .multiselect__single,
  .multiselect__placeholder {
    font-size: 0.875rem;
    color: $gray-700;
    font-weight: 500;
    margin-bottom: 8px;
    padding-top: 0;
  }

  .multiselect__placeholder {
    color: $gray-400;
    font-weight: 400;
  }

  &:hover {
    border-color: transparent;
    box-shadow: none;
  }

  &.multiselect--active {
    border-color: #01beb0;
    box-shadow: none;
  }
}

// Responsive Design
@media screen and (max-width: 768px) {
  .c-claim-details-form {
    padding: 1.5rem;
  }

  .c-content-section {
    padding: 1.25rem;
    border-radius: 12px;
  }

  .organization-logo img {
    width: 120px;
    height: 120px;
  }
}

@media screen and (max-width: 480px) {
  .c-claim-details-form {
    padding: 1rem;
  }

  .c-content-section {
    padding: 1rem;
    border-radius: 12px;
  }

  .organization-logo img {
    width: 100px;
    height: 100px;
  }
}
</style>
